<template>
  <nav>
    <ul :class="$style.list">
      <li v-for="stage in stages" :key="stage.id" :class="$style.contents">
        <button
          type="button"
          :class="[
            $style.item,
            {
              [$style.active]: stage.id === modelValue,
              [$style.finished]: stage.status === 'finished'
            }
          ]"
          @click="emit('update:modelValue', stage.id)"
        >
          <span :class="$style.body">
            <span :class="$style.title">{{ stage.name }}</span>
            <span :class="$style.subtitle" v-if="stage.id === modelValue">
              {{ subtitleOf(stage) }}
            </span>
          </span>
          <span :class="$style.statusInfo">
            <ui-icon
              v-if="iconOf(stage)"
              :name="iconOf(stage)!"
              :class="[
                $style.statusIcon,
                { [$style.statusIconLock]: stage.ui_state === 'locked' },
                {
                  [$style.statusIconInProgress]:
                    stage.ui_state === 'awaiting_results'
                },
                {
                  [$style.statusIconSmall]: stage.ui_state === 'finished'
                }
              ]"
            />
            <template
              v-if="
                stage.ui_state === 'finished' &&
                stage.user_xp &&
                stage.user_xp > 0
              "
            >
              <ui-icon
                name="xp"
                :class="[$style.statusIcon, $style.statusIconSmall]"
              />
              <span :class="$style.statusXpEarned">
                {{ stage.user_xp }}
              </span>
            </template>
          </span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<i18n lang="yaml">
ru:
  status:
    locked: Заблокирован
    pickable: Выбери команды
    finished: Завершён
    inProgress: В процессе
    awaiting_open: Скоро откроется
</i18n>

<script lang="ts" setup>
import UiIcon from '@/ui-core/components/icon/index.vue'

import type { StageSummary } from '../../types'

defineOptions({ name: 'PickemStagesSidebarList' })

defineProps<{
  stages: StageSummary[]
  modelValue: number | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const { t } = useI18n()

const subtitleOf = (stage: StageSummary): string => {
  switch (stage.ui_state) {
    case 'finished':
      return t('status.finished')
    case 'locked':
      return t('status.locked')
    case 'awaiting_open':
      return t('status.awaiting_open')
    case 'awaiting_results':
      return t('status.inProgress')
    case 'pickable':
      return t('status.pickable')
  }
}

const iconOf = (stage: StageSummary): string | null => {
  switch (stage.ui_state) {
    case 'finished':
      return 'check'
    case 'locked':
    case 'awaiting_open':
      return 'lock-circled'
    case 'awaiting_results':
      return 'play-filled'
    default:
      return null
  }
}
</script>

<style lang="scss" module>
.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4em;
}

.contents {
  display: contents;
}

.item {
  display: flex;
  align-items: center;
  gap: 1em;
  padding: 0.8em 1.2em;
  border-radius: 0.4em;
  text-align: left;
  width: 100%;
  background: transparent;
  color: color-get(--root-rgb-text-2);
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  @include hover-focus {
    background-color: color-get(--root-rgb-bg-default);
  }

  &.active {
    color: color-get(--root-rgb-text-1);
    background-color: #3e4756;
  }

  &.finished {
    color: color-get(--root-rgb-text-3);
  }
}

.body {
  display: flex;
  align-items: center;
  gap: 0.8em;
}

.statusInfo {
  margin-left: auto;

  display: flex;
  align-items: center;

  gap: 0.4em;
}

.statusXpEarned {
  @include typo-get(subhead-s);
  color: color-get(--root-rgb-text-2);
}

.title {
  @include typo-get(label-m);
}

.subtitle {
  @include typo-get(label-m);
  color: color-get(--root-rgb-text-2);
}

.statusIcon {
  flex-shrink: 0;
  width: 1.6em;
  height: 1.6em;
  color: color-get(--root-rgb-text-2);
}

.statusIconSmall {
  width: 1.2em;
  height: 1.2em;
}

.statusIconLock {
  color: color-get(--root-rgb-accent-4);
}

.statusIconInProgress {
  color: color-get(--root-rgb-system-warning);
}

.item.active .position {
  background-color: color-get(--root-rgb-accent-2);
  color: color-get(--root-rgb-text-inverse);
}
</style>
